"use client";

import { FileText } from "lucide-react";

import { LinkPreviewCard } from "@/components/feed/link-preview-card";
import { Button } from "@/components/ui/button";
import { MediaMosaic } from "@/components/ui/media-mosaic";
import { isExternalBannerUrl } from "@/lib/banner/types";
import type { FeedAd } from "@/lib/feed/ads";
import { formatFileSize } from "@/lib/utils";

export function FeedAdCard({ ad }: { ad: FeedAd }) {
  const media = ad.attachments.filter(
    (attachment) =>
      (attachment.type === "image" || attachment.type === "video") &&
      attachment.url,
  );
  const documents = ad.attachments.filter(
    (attachment) => !media.some((item) => item.id === attachment.id),
  );
  const ctaHref = ad.ctaUrl?.trim() || null;
  const ctaExternal = ctaHref ? isExternalBannerUrl(ctaHref) : false;

  return (
    <article className="shadow-surface rounded-none border border-x-0 bg-card p-5 md:rounded-2xl md:border-x">
      <p className="mb-3 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Hirdetés
      </p>

      {ad.content ? (
        <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{ad.content}</p>
      ) : null}

      {media.length > 0 ? (
        <MediaMosaic
          className={`border ${ad.content ? "mt-3" : ""}`}
          items={media.map((attachment) => ({
            key: attachment.id,
            src: attachment.url!,
            alt: attachment.fileName ?? "Hirdetés képe",
            kind: attachment.type === "video" ? "video" : "image",
          }))}
        />
      ) : null}

      {documents.map((attachment) => (
        <a
          key={attachment.id}
          href={attachment.url ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="mt-3 flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-sm hover:bg-muted/50"
        >
          <FileText className="size-4 text-primary" />
          <span className="min-w-0 flex-1 truncate">
            {attachment.fileName ?? "Dokumentum"}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatFileSize(attachment.sizeBytes)}
          </span>
        </a>
      ))}

      {ad.linkPreview ? (
        <LinkPreviewCard className="mt-3" preview={ad.linkPreview} />
      ) : null}

      {ctaHref && ad.ctaLabel ? (
        <div className="mt-4">
          <Button asChild>
            <a
              href={ctaHref}
              target={ctaExternal ? "_blank" : undefined}
              rel={ctaExternal ? "noreferrer" : undefined}
            >
              {ad.ctaLabel}
            </a>
          </Button>
        </div>
      ) : null}
    </article>
  );
}
